import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import AdminLayout from "../components/AdminLayout";
import WorkerLayout from "../components/WorkerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Clock, Plus, LogIn, LogOut, CalendarIcon, Filter, MapPin } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function TimeRecordsPage({ workerView = false }) {
  const [records, setRecords] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterDate, setFilterDate] = useState(new Date());
  const [filterWorker, setFilterWorker] = useState("all");
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    worker_id: "",
    location_id: "",
    type: "entry",
    timestamp: ""
  });

  const Layout = workerView ? WorkerLayout : AdminLayout;

  useEffect(() => {
    fetchData();
  }, [filterDate, filterWorker]);

  const fetchData = async () => {
    try {
      const dateStr = format(filterDate, "yyyy-MM-dd");
      let url = `${API}/time-records?date=${dateStr}`;
      
      if (filterWorker && filterWorker !== "all" && isAdmin) {
        url += `&worker_id=${filterWorker}`;
      }

      const [recordsRes, workersRes, locationsRes] = await Promise.all([
        axios.get(url),
        isAdmin ? axios.get(`${API}/workers`) : Promise.resolve({ data: [] }),
        axios.get(`${API}/locations`)
      ]);

      setRecords(recordsRes.data);
      setWorkers(workersRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar registros");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/time-records/manual`, {
        ...formData,
        timestamp: new Date(formData.timestamp).toISOString()
      });
      
      toast.success("Registro adicionado com sucesso!");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao adicionar registro");
    }
  };

  const resetForm = () => {
    setFormData({
      worker_id: "",
      location_id: "",
      type: "entry",
      timestamp: ""
    });
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Group records by worker for admin view
  const groupedRecords = records.reduce((acc, record) => {
    if (!acc[record.worker_id]) {
      acc[record.worker_id] = {
        worker_name: record.worker_name,
        records: []
      };
    }
    acc[record.worker_id].records.push(record);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="time-records-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-manrope">
              {workerView ? "Meu Histórico" : "Registros de Ponto"}
            </h1>
            <p className="text-gray-500 mt-1">
              {format(filterDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
            </p>
          </div>
          
          <div className="flex gap-3 items-center">
            {/* Date Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-gray-200 text-gray-700 bg-white hover:bg-gray-50" data-testid="date-filter-btn">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(filterDate, "dd/MM/yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white border-gray-200" align="end">
                <Calendar
                  mode="single"
                  selected={filterDate}
                  onSelect={(date) => date && setFilterDate(date)}
                  locale={ptBR}
                  className="bg-white rounded-md"
                />
              </PopoverContent>
            </Popover>

            {/* Worker Filter (Admin only) */}
            {isAdmin && (
              <Select value={filterWorker} onValueChange={setFilterWorker}>
                <SelectTrigger className="w-[180px] bg-white border-gray-200 text-gray-700" data-testid="worker-filter">
                  <Filter className="mr-2 h-4 w-4 text-gray-400" />
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all">Todos</SelectItem>
                  {workers.map((worker) => (
                    <SelectItem key={worker.id} value={worker.id}>
                      {worker.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Add Manual Record (Admin only) */}
            {isAdmin && (
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary text-white font-semibold" data-testid="add-manual-record-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    Manual
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-gray-200">
                  <DialogHeader>
                    <DialogTitle className="text-gray-800">Adicionar Registro Manual</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Trabalhador</Label>
                      <Select value={formData.worker_id} onValueChange={(v) => setFormData({ ...formData, worker_id: v })}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="manual-worker-select">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {workers.map((worker) => (
                            <SelectItem key={worker.id} value={worker.id}>
                              {worker.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Local</Label>
                      <Select value={formData.location_id} onValueChange={(v) => setFormData({ ...formData, location_id: v })}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="manual-location-select">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {locations.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Tipo</Label>
                      <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="manual-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          <SelectItem value="entry">Entrada</SelectItem>
                          <SelectItem value="exit">Saída</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Data e Hora</Label>
                      <Input
                        type="datetime-local"
                        value={formData.timestamp}
                        onChange={(e) => setFormData({ ...formData, timestamp: e.target.value })}
                        required
                        className="bg-gray-50 border-gray-200 text-gray-800"
                        data-testid="manual-timestamp-input"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <DialogClose asChild>
                        <Button type="button" variant="outline" className="flex-1 border-gray-200 text-gray-600 hover:bg-gray-50">
                          Cancelar
                        </Button>
                      </DialogClose>
                      <Button type="submit" className="flex-1 btn-primary text-white font-semibold" data-testid="save-manual-record-btn">
                        Adicionar
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Records */}
        {records.length === 0 ? (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardContent className="py-16">
              <div className="empty-state">
                <Clock className="empty-state-icon" />
                <p className="empty-state-title">Nenhum registro encontrado</p>
                <p className="empty-state-description">
                  Não há registros de ponto para esta data
                </p>
              </div>
            </CardContent>
          </Card>
        ) : workerView ? (
          // Worker view - simple list
          <div className="space-y-3">
            {records.map((record) => (
              <Card key={record.id} className="bg-white border-gray-100 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      record.type === "entry" 
                        ? "bg-green-100 text-[#2ECC40]" 
                        : "bg-amber-100 text-[#F5A623]"
                    }`}>
                      {record.type === "entry" ? <LogIn size={18} /> : <LogOut size={18} />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        {record.type === "entry" ? "Entrada" : "Saída"}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <MapPin size={12} />
                        {record.location_name}
                      </p>
                    </div>
                    <span className="text-lg font-mono text-gray-600">
                      {formatTime(record.timestamp)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          // Admin view - grouped by worker
          <div className="space-y-6">
            {Object.entries(groupedRecords).map(([workerId, data]) => (
              <Card key={workerId} className="bg-white border-gray-100 shadow-sm" data-testid={`worker-records-${workerId}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#2ECC40] to-[#F5A623] flex items-center justify-center text-white font-bold">
                      {data.worker_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <CardTitle className="text-lg text-gray-800">{data.worker_name}</CardTitle>
                      <p className="text-sm text-gray-500">{data.records.length} registros</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {data.records.map((record) => (
                      <div
                        key={record.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            record.type === "entry" 
                              ? "bg-green-100 text-[#2ECC40]" 
                              : "bg-amber-100 text-[#F5A623]"
                          }`}>
                            {record.type === "entry" ? <LogIn size={14} /> : <LogOut size={14} />}
                          </div>
                          <div>
                            <p className="text-sm text-gray-800">
                              {record.type === "entry" ? "Entrada" : "Saída"}
                            </p>
                            <p className="text-xs text-gray-500">{record.location_name}</p>
                          </div>
                        </div>
                        <span className="text-sm font-mono text-gray-500">
                          {formatTime(record.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
